/**
 * Shared Edit/Save/Cancel button group component
 * Provides consistent styling and behavior across all detail view tabs
 *
 * @param {boolean} isEditMode - Whether currently in edit mode
 * @param {boolean} isSaving - Whether save operation is in progress
 * @param {Function} onEdit - Callback when Edit button is clicked
 * @param {Function} onSave - Callback when Save button is clicked
 * @param {Function} onCancel - Callback when Cancel button is clicked
 * @param {boolean} disabled - Whether buttons should be disabled (optional)
 */
export default function EditSaveButtons({
  isEditMode = false,
  isSaving = false,
  onEdit,
  onSave,
  onCancel,
  disabled = false
}) {
  if (isEditMode) {
    return (
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          disabled={isSaving || disabled}
          className="border rounded-lg px-6 py-2.5 font-semibold transition-all duration-200 flex items-center gap-2"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-border-default)',
            color: 'var(--color-text-primary)'
          }}
          onMouseEnter={(e) => {
            if (!isSaving && !disabled) {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSaving && !disabled) {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
            }
          }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={isSaving || disabled}
          className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-700 disabled:to-gray-700 px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 shadow-lg shadow-purple-900/30 disabled:shadow-none"
          style={{ color: 'var(--color-text-on-accent)' }}
        >
          {isSaving ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={onEdit}
      disabled={disabled}
      className="border rounded-lg px-6 py-2.5 font-semibold transition-all duration-200 flex items-center gap-2"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderColor: 'var(--color-border-default)',
        color: 'var(--color-text-primary)'
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
        }
      }}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
      Edit
    </button>
  );
}
